import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  Truck, Route as RouteIcon, PackageOpen, Container, PackageCheck, Users, ArrowRightLeft, ChevronRight,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';

/**
 * /nursery/logistics — getting orders out of the gate: delivery zones, the
 * despatch planner, loads, picking, pickers and transfers. They were seven
 * cards on the nursery dashboard. Sorted by label in the reader's language.
 *
 * Label and detail of each are `tools.<key>.label` / `tools.<key>.detail`.
 */

interface Tool { key: string; icon: LucideIcon; path: string }

const TOOLS: Tool[] = [
  { key: 'deliveryZones', icon: Truck, path: '/nursery/delivery-zones' },
  { key: 'despatch', icon: RouteIcon, path: '/nursery/despatch' },
  { key: 'despatchable', icon: PackageOpen, path: '/nursery/despatchable' },
  { key: 'loads', icon: Container, path: '/nursery/loads' },
  { key: 'pickers', icon: Users, path: '/nursery/pickers' },
  { key: 'picking', icon: PackageCheck, path: '/nursery/picking' },
  { key: 'transfers', icon: ArrowRightLeft, path: '/nursery/transfers' },
];

function LogisticsHub() {
  const { t, i18n } = useTranslation('nursery', { keyPrefix: 'LogisticsHub' });
  const label = (tool: Tool) => t(`tools.${tool.key}.label`);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Truck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {[...TOOLS].sort((a, b) => label(a).localeCompare(label(b), i18n.language)).map((tool) => {
          const Icon = tool.icon;
          return (
            <Link key={tool.path} to={tool.path}
              className="flex items-start gap-3 bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all">
              <Icon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-neutral-800">{label(tool)}</span>
                <span className="block text-sm text-neutral-500 mt-0.5">{t(`tools.${tool.key}.detail`)}</span>
              </span>
              <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0 self-center" />
            </Link>
          );
        })}
      </div>
    </>
  );
}

export default LogisticsHub;
